/**
 * @file textarea.component.tsx
 * @path packages/ui/src/textarea.component.tsx
 * @description
 */

import { cn } from "@sirbastians/utils";
import * as React from "react";
import { Label } from "./label.component";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: boolean;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, label, id, error, ...props }, ref) => {
    const generatedId = React.useId();
    const textareaId = id || generatedId;

    return (
      <div className="relative w-full group">
        <textarea
          id={textareaId}
          className={cn(
            // Base Layout
            // pt-6 ensures text doesn't overlap the floating label
            "peer flex min-h-[120px] w-full rounded-xl border px-4 pt-6 pb-2 text-sm transition-all duration-200 outline-none resize-y",

            // NEUROMORPHIC STYLE (The "Soft Well")
            "neuro-well bg-secondary text-secondary-foreground border-input",

            // INTERACTION STATES
            "hover:border-foreground/20",
            "focus:border-muted-foreground focus:ring-1 focus:ring-muted-foreground/20",

            // ERROR STATE
            error &&
              "border-destructive focus:border-destructive focus:ring-destructive/20",

            // Disabled
            "disabled:cursor-not-allowed disabled:opacity-50",
            className,
          )}
          placeholder=" " // Required for CSS-only floating label
          ref={ref}
          {...props}
        />

        {label && (
          <Label
            htmlFor={textareaId}
            className={cn(
              // Structural
              "absolute left-4 transition-all duration-200 ease-out origin-[0] pointer-events-none",

              // --- 1. DEFAULT STATE (Floating / Filled) ---
              // Positioned UP inside the padding area
              "top-1.5 scale-[65%] font-medium text-muted-foreground bg-transparent",

              // --- 2. RESTING STATE (Empty / Placeholder Shown) ---
              // Moves DOWN to look like a standard placeholder
              "peer-placeholder-shown:top-4 peer-placeholder-shown:scale-100 peer-placeholder-shown:text-muted-foreground peer-placeholder-shown:font-normal",

              // --- 3. FOCUS STATE (Active) ---
              // Moves UP and turns Green (Accent)
              "peer-focus:top-1.5 peer-focus:scale-[65%] peer-focus:text-primary peer-focus:font-bold",

              // Error Styling
              error && "text-destructive peer-focus:text-destructive",
            )}
          >
            {label}
          </Label>
        )}
      </div>
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
